import React, {useState, useRef, useMemo, useEffect} from 'react'
import * as d3 from 'd3'
import prefersReducedMotion from '../../utils/PrefersReducedMotion.js'
import moment from 'moment'
import Colors from '../../../utils/Colors.js'
import _ from 'lodash'
import useAnalyticsStore from 'src/stores/AnalyticsStore.js'
import {Card} from '@components/UI/index.js'

export interface MultiLineChartProps {
    width: number
    height: number
    data: {
        name: string
        value: number
        date: string
        forecast?: {
            min: number
            max: number
        }
    }[]
    showGroupList?: boolean
    showDots?: boolean
    applyCurvature?: boolean
    animated?: boolean
    interval?: 'day' | 'week' | 'month'
    colorMap?: {
        [key: string]: string
    }
    minimumY?: number
    maximumY?: number
    amountYTicks?: number
    appendValueLabel?: string
}

/**
 * @param {MultiLineChartProps} props
 *
 * @returns {React.JSX.Element} The rendered MultiLineChart component
 */
export default function MultiLineChart({
    width,
    height,
    data,
    showGroupList = true,
    showDots = false,
    applyCurvature = true,
    animated = !prefersReducedMotion,
    interval = 'day',
    colorMap,
    minimumY = 0,
    maximumY,
    amountYTicks,
    appendValueLabel
}: MultiLineChartProps): React.JSX.Element {
    const {analyticsStartDate, analyticsEndDate} = useAnalyticsStore((state) => state)
    const margin = {top: 40, right: 20, bottom: 60, left: showGroupList ? 272 : 100}

    const [animationDone, setAnimationDone] = useState(false)
    const svgRef = useRef<SVGSVGElement>(null)

    const dataIsEmpty = useMemo(() => data.length === 0, [data])

    const minimumX = dataIsEmpty ? new Date(analyticsStartDate) : d3.min(data, (d) => new Date(d.date)) as Date
    let maximumX = dataIsEmpty ? new Date(analyticsEndDate) : d3.max(data, (d) => new Date(d.date)) as Date

    let tickInterval = {
        day: d3.utcDay,
        week: d3.utcWeek,
        month: d3.utcMonth
    }[interval]

    const uniqueDates = _.uniq(data.map((d) => d.date))
    if (dataIsEmpty || uniqueDates.length === 1) {
        const minAmountX = 7
        const intervalCount = tickInterval.count(minimumX, maximumX)
        if (intervalCount < minAmountX) {
            const newMax = moment(minimumX).add(minAmountX - 1, interval)
            maximumX = newMax.toDate()
        }
    }

    const xScale = useMemo(() => {
        return d3.scaleUtc()
            .domain([minimumX, maximumX])
            .range([margin.left, width - margin.right])
    }, [data, width])

    const yScale = useMemo(() => {
        return d3.scaleLinear()
            .domain([minimumY, maximumY ?? (dataIsEmpty ? 100 : d3.max(data, (d) => d.value) as number)]).nice()
            .range([height - margin.bottom, margin.top])
    }, [data, height, dataIsEmpty])

    const line = useMemo(() => {
        const l = d3.line()
            .x((d) => d[0])
            .y((d) => d[1])

        if (applyCurvature) {
            l.curve(d3.curveMonotoneX)
        }

        return l
    }, [])

    const points = useMemo(() => {
        return data.map((d) => [xScale(new Date(d.date)), yScale(d.value), d.name])
    }, [data])

    const groupedData = useMemo(() => {
        const groups = d3.groups(data.filter((d) => !d.forecast), (d) => d.name)
        const mappedGroups: {
            name: string
            values: number[][]
            forecastType?: 'min' | 'average' | 'max'
        }[] = groups.map(([name, values]) => ({
            name,
            values: values.map((d) => [xScale(new Date(d.date)), yScale(d.value)])
        }))

        const forecastGroups = d3.groups(data.filter((d) => d.forecast), (d) => d.name)
        forecastGroups.forEach(([name, values]) => {
            mappedGroups.push({
                name,
                values: values.map((d) => [xScale(new Date(d.date)), yScale(d.value)]),
                forecastType: 'average'
            })

            mappedGroups.push({
                name,
                values: values.map((d) => [xScale(new Date(d.date)), yScale(d.forecast!.min)]),
                forecastType: 'min'
            })

            mappedGroups.push({
                name,
                values: values.map((d) => [xScale(new Date(d.date)), yScale(d.forecast!.max)]),
                forecastType: 'max'
            })
        })

        return mappedGroups
    }, [data, xScale, yScale])

    const maxTicks = Math.floor((width - margin.left - margin.right) / 20)

    while (tickInterval.count(minimumX as Date, d3.max(data, (d) => new Date(d.date)) as Date) > maxTicks) {
        if (interval === 'day') {
            tickInterval = d3.utcWeek
        } else if (interval === 'week') {
            tickInterval = d3.utcMonth
        } else {
            tickInterval = d3.utcMonth
        }
    }

    const xTickFormat = {
        day: 'DD-MM',
        week: '[WK]W',
        month: 'MMM'
    }[interval]

    const xTicks = xScale.ticks(tickInterval.every(1)!)

    const xTickElements = xTicks.map((tick) => (
        <g key={tick.getTime()} transform={`translate(${xScale(tick)}, ${height})`}>
            <text style={{textAnchor: 'middle', fontSize: 16, transform: 'rotate(-65deg) translate(20px, 0px)'}}>{moment(tick).format(xTickFormat)}</text>
        </g>
    ))

    const yTicks = yScale.ticks(amountYTicks ?? undefined).map((tick) => (
        <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
            <text x={xScale(minimumX) - 24} dy='0.35em' textAnchor='end'>{tick.toLocaleString('nl-NL')}{appendValueLabel}</text>
            <line x1={xScale(minimumX)} x2={width - margin.right} stroke={Colors.grey40} strokeWidth={1} strokeOpacity={0.5} />
        </g>
    ))

    const GroupList = () => {
        const sortedGroups = groupedData.filter((g) => typeof g.forecastType === 'undefined').map((group) => {
            const d = data.filter((d) => d.name === group.name)
            const lastValue = _.findLast(d, (data) => typeof data.value === 'number')

            return {
                name: group.name.substring(0, 8),
                value: lastValue?.value || 0
            }
        }).sort((a, b) => b.value - a.value)

        return (
            <g transform={`translate(0, ${margin.top})`}>
                {sortedGroups.map((group, i) => (
                    <text key={`${group.name}-${i}`} x={0} y={(16 + 20) * i} dy='.35em'>{group.name} - {group.value.toLocaleString('nl-NL')}</text>
                ))}
            </g>
        )
    }

    useEffect(() => {
        const duration = 1_000
        if (svgRef.current) {
            setAnimationDone(false)
            const svg = d3.select(svgRef.current)
            const paths = svg.selectAll('path')
            const dot = svg.select('.dot')

            if (animated) {
                paths
                    .attr('stroke-dasharray', (_, i, nodes) => {
                        const el = nodes[i] as SVGGeometryElement
                        if (!el) {
                            return null
                        }

                        const totalLength = el.getTotalLength()
                        return `${totalLength} ${totalLength}`
                    })
                    .attr('stroke-dashoffset', (_, i, nodes) => {
                        const el = nodes[i] as SVGGeometryElement
                        if (!el) {
                            return null
                        }
                        const totalLength = el.getTotalLength()
                        return totalLength
                    })
                    .transition()
                    .duration(duration)
                    .ease(d3.easeCubicInOut)
                    .attr('stroke-dashoffset', 0)
                    .attr('stroke-dasharray', (_, i, nodes) => {
                        const el = nodes[i] as SVGGeometryElement
                        if (!el) {
                            return null
                        }

                        const forecast = el.getAttribute('data-forecast-type')

                        if (forecast) {
                            return '3'
                        } else {
                            const totalLength = el.getTotalLength()
                            return `${totalLength} ${totalLength}`
                        }
                    })
                    .on('end', () => {
                        setAnimationDone(true)
                    })
            } else {
                paths.attr('d', (_, i) => {
                    const values = groupedData[i].values
                    return line(values as [number, number][])
                })
            }

            const TOOLTIP_WIDTH = 281
            const PADDING = 8

            function clamp(val, min, max) {
                return Math.max(min, Math.min(max, val))
            }

            const pointermoved = (event) => {
                const [xm, ym] = d3.pointer(event)
                const i = d3.leastIndex(points, ([x, y]) => Math.hypot((typeof x === 'string' ? parseFloat(x) : x) - xm, typeof y === 'string' ? parseFloat(y) : y - ym)) as number

                if (!points[i]) return

                const [x, y, k] = points[i]

                // Clamp X so tooltip stays inside
                const clampedX = clamp(
                    x,
                    TOOLTIP_WIDTH / 2 + PADDING,
                    width - TOOLTIP_WIDTH / 2 - PADDING
                )

                paths.style('stroke', (_, i) => {
                    const z = groupedData[i].name
                    return z === k ? null : '#ddd'
                }).filter(() => {
                    const z = groupedData.find((g) => g.name === k)?.name
                    return z === k
                }).raise()

                dot.attr('transform', `translate(${x},${y})`)
                dot.selectAll('rect,text').attr('transform', `translate(${clampedX - Number(x)}, 0)`)

                const date = moment(xScale.invert(typeof x === 'string' ? parseFloat(x) : x)).locale('nl-NL').format('ddd DD MMM')
                dot.select('text').text(`${k ? `${k.toString()} - ` : ''}${yScale.invert(typeof y === 'string' ? parseFloat(y) : y).toLocaleString('nl-NL')}${appendValueLabel || ''} (${date})`)
            }

            const pointerentered = () => {
                paths.style('mix-blend-mode', null).style('stroke', '#ddd')
                dot.attr('display', null)
            }

            const pointerleft = () => {
                paths.style('mix-blend-mode', 'multiply').style('stroke', null)
                dot.attr('display', 'none')
            }

            svg
                .on('pointerenter', pointerentered)
                .on('pointermove', pointermoved)
                .on('pointerleave', pointerleft)
                .on('touchstart', (event) => event.preventDefault())
        }
    }, [data, animated, line, groupedData, width, height])

    return (
        <div style={{position: 'relative'}}>
            {dataIsEmpty &&
                <Card style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', padding: 24}}>
                    Geen data gevonden in de geselecteerde periode.
                </Card>
            }
            <svg ref={svgRef} width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                <g className='y-ticks'>{yTicks}</g>
                <g fill='none' strokeWidth={1.5} strokeLinejoin='round' strokeLinecap='round'>
                    {groupedData.map((group, i) => (
                        <path
                            key={i}
                            d={line(group.values as [number, number][]) as string}
                            data-forecast-type={group.forecastType || undefined}
                            stroke={group.forecastType === 'min' ? 'red' : group.forecastType === 'max' ? 'green' : colorMap?.[group.name] || '#3077B6'}
                            style={{mixBlendMode: 'multiply'}}
                        />
                    ))}
                </g>
                {showDots &&
                    <g>
                        {points.map(([x, y, name], i) => {
                            return x && y ? <circle fill={colorMap?.[name] || '#3077B6'} style={{opacity: animationDone ? 1 : 0, transform: `translate(${x}px, ${y}px) scale(${animationDone ? 1 : 0})`, transition: 'opacity 0.2s, transform 0.2s'}} key={`${i}-${x}-${y}`} r={2.5} /> : null
                        })}
                    </g>
                }
                <g className='x-ticks'>{xTickElements}</g>
                {showGroupList && <GroupList />}
                <g className='dot' display='none'>
                    <circle r={2.5}></circle>
                    <rect fill={Colors.white} width={281} x={-(281 / 2)} y={-8 - 24} height={24} stroke={Colors.grey40} strokeWidth={1} rx={4} />
                    <text width={281} textAnchor='middle' y={-24 + 8} fill='black'></text>
                </g>
            </svg>
        </div>
    )
}
